// =============================================================================
// Configuração do Assistente de Atendimento (persistida em `ai_settings`, 0061).
// Regra PURA: padrões, validação e normalização. Nada aqui é segredo — as chaves
// de API ficam no cofre `ai_secrets` e nunca passam por este objeto.
// =============================================================================
/**
 * Único usuário que vê e altera chaves e parâmetros da IA (pedido do operador). A Edge Function
 * `ai-runtime` impõe a regra no servidor (`AI_OWNER_EMAIL`, mesmo padrão); a Plataforma só a usa
 * para esconder o menu e a rota de quem não pode.
 */
export const AI_OWNER_EMAIL = "thiago@tdzain.com.br";
/** Intenções que o porteiro reconhece e que têm limiar próprio. */
export const AI_INTENTS = ["preco", "ficha_tecnica", "frete", "status_pedido", "saudacao", "reclamacao", "pedido_humano", "outro"];
/** Intenções que a IA pode responder sozinha (as demais vão para humano). */
export const ANSWERABLE_INTENTS = ["preco", "ficha_tecnica", "frete", "status_pedido", "saudacao"];
export const AI_MAX_TEMPERATURE = 0.4;
export const DEFAULT_AI_SETTINGS = {
    enabled: false,
    mode: "shadow",
    learningStartedAt: null,
    learningDays: 14,
    models: {
        gatekeeper: "typesafe/jev-router",
        responder: "anthropic/claude-sonnet-5.5",
        transcriber: "google/gemini-3.5-flash",
        summarizer: "google/gemini-3.5-flash-lite",
        embedder: "voyageai/voyage-4-lite"
    },
    gatekeeperProvider: "typesafe",
    thresholds: { preco: 0.8, ficha_tecnica: 0.75, frete: 0.8, status_pedido: 0.9, saudacao: 0.6 },
    gates: { canAnswerMin: 0.7, urgencyMax: 0.6, frustrationMax: 0.6 },
    tone: { formality: 35, length: 30, emoji: 25, temperature: 0.25 },
    pilot: { tag: "ia-piloto", numbers: [] },
    pauseHoursAfterHuman: 4,
    dailyBudgetUsd: 5,
    identityPolicy: "admit",
    styleGuide: ""
};
const MODEL_ID = /^[a-z0-9][a-z0-9._-]*\/[A-Za-z0-9][A-Za-z0-9._:-]*$/;
const unit = (value, fallback) => (typeof value === "number" && Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : fallback);
const percent = (value, fallback) => (typeof value === "number" && Number.isFinite(value) ? Math.min(100, Math.max(0, Math.round(value))) : fallback);
const isRecord = (value) => typeof value === "object" && value !== null && !Array.isArray(value);
/** Telefone só com dígitos, com DDI 55 (comparação de lista piloto não pode depender de formatação). */
export function normalizePilotNumber(value) {
    const digits = value.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 13)
        return null;
    return digits.startsWith("55") ? digits : `55${digits}`;
}
/**
 * Valida e completa a configuração recebida (de gravação ou do banco). Retorna `error`
 * no primeiro campo inválido; campos ausentes recebem o padrão. Nunca confia em tipo.
 */
export function parseAiSettings(input, base = DEFAULT_AI_SETTINGS) {
    if (!isRecord(input))
        return { ok: false, error: "settings_must_be_an_object" };
    const next = structuredClone(base);
    if (input.enabled !== undefined) {
        if (typeof input.enabled !== "boolean")
            return { ok: false, error: "invalid_enabled" };
        next.enabled = input.enabled;
    }
    if (input.mode !== undefined) {
        if (input.mode !== "shadow" && input.mode !== "suggest" && input.mode !== "pilot")
            return { ok: false, error: "invalid_mode" };
        next.mode = input.mode;
    }
    if (input.learningStartedAt !== undefined) {
        if (input.learningStartedAt !== null && (typeof input.learningStartedAt !== "string" || Number.isNaN(Date.parse(input.learningStartedAt))))
            return { ok: false, error: "invalid_learning_started_at" };
        next.learningStartedAt = input.learningStartedAt;
    }
    if (input.learningDays !== undefined) {
        if (typeof input.learningDays !== "number" || !Number.isInteger(input.learningDays) || input.learningDays < 1 || input.learningDays > 90)
            return { ok: false, error: "invalid_learning_days" };
        next.learningDays = input.learningDays;
    }
    if (input.models !== undefined) {
        if (!isRecord(input.models))
            return { ok: false, error: "invalid_models" };
        for (const key of Object.keys(next.models)) {
            const model = input.models[key];
            if (model === undefined)
                continue;
            if (typeof model !== "string" || !MODEL_ID.test(model))
                return { ok: false, error: `invalid_model:${key}` };
            next.models[key] = model;
        }
    }
    if (input.gatekeeperProvider !== undefined) {
        if (input.gatekeeperProvider !== "typesafe" && input.gatekeeperProvider !== "llm")
            return { ok: false, error: "invalid_gatekeeper_provider" };
        next.gatekeeperProvider = input.gatekeeperProvider;
    }
    if (input.thresholds !== undefined) {
        if (!isRecord(input.thresholds))
            return { ok: false, error: "invalid_thresholds" };
        for (const key of Object.keys(next.thresholds))
            next.thresholds[key] = unit(input.thresholds[key], next.thresholds[key]);
    }
    if (input.gates !== undefined) {
        if (!isRecord(input.gates))
            return { ok: false, error: "invalid_gates" };
        next.gates = { canAnswerMin: unit(input.gates.canAnswerMin, next.gates.canAnswerMin), urgencyMax: unit(input.gates.urgencyMax, next.gates.urgencyMax), frustrationMax: unit(input.gates.frustrationMax, next.gates.frustrationMax) };
    }
    if (input.tone !== undefined) {
        if (!isRecord(input.tone))
            return { ok: false, error: "invalid_tone" };
        const temperature = typeof input.tone.temperature === "number" ? input.tone.temperature : next.tone.temperature;
        if (!Number.isFinite(temperature) || temperature < 0 || temperature > AI_MAX_TEMPERATURE)
            return { ok: false, error: "invalid_temperature" };
        next.tone = { formality: percent(input.tone.formality, next.tone.formality), length: percent(input.tone.length, next.tone.length), emoji: percent(input.tone.emoji, next.tone.emoji), temperature };
    }
    if (input.pilot !== undefined) {
        if (!isRecord(input.pilot))
            return { ok: false, error: "invalid_pilot" };
        if (input.pilot.tag !== undefined) {
            if (typeof input.pilot.tag !== "string" || !/^[\p{L}\p{N}_-]{1,24}$/u.test(input.pilot.tag.trim()))
                return { ok: false, error: "invalid_pilot_tag" };
            next.pilot.tag = input.pilot.tag.trim();
        }
        if (input.pilot.numbers !== undefined) {
            if (!Array.isArray(input.pilot.numbers) || input.pilot.numbers.length > 50)
                return { ok: false, error: "invalid_pilot_numbers" };
            const numbers = [];
            for (const raw of input.pilot.numbers) {
                const normalized = typeof raw === "string" ? normalizePilotNumber(raw) : null;
                if (!normalized)
                    return { ok: false, error: "invalid_pilot_number" };
                if (!numbers.includes(normalized))
                    numbers.push(normalized);
            }
            next.pilot.numbers = numbers;
        }
    }
    if (input.pauseHoursAfterHuman !== undefined) {
        if (typeof input.pauseHoursAfterHuman !== "number" || input.pauseHoursAfterHuman < 0 || input.pauseHoursAfterHuman > 168)
            return { ok: false, error: "invalid_pause_hours" };
        next.pauseHoursAfterHuman = input.pauseHoursAfterHuman;
    }
    if (input.dailyBudgetUsd !== undefined) {
        if (typeof input.dailyBudgetUsd !== "number" || !Number.isFinite(input.dailyBudgetUsd) || input.dailyBudgetUsd < 0 || input.dailyBudgetUsd > 1000)
            return { ok: false, error: "invalid_budget" };
        next.dailyBudgetUsd = input.dailyBudgetUsd;
    }
    if (input.identityPolicy !== undefined) {
        if (input.identityPolicy !== "admit" && input.identityPolicy !== "handoff")
            return { ok: false, error: "invalid_identity_policy" };
        next.identityPolicy = input.identityPolicy;
    }
    if (input.styleGuide !== undefined) {
        if (typeof input.styleGuide !== "string" || input.styleGuide.length > 4000)
            return { ok: false, error: "invalid_style_guide" };
        next.styleGuide = input.styleGuide;
    }
    return { ok: true, value: next };
}
/** Dia atual da fase de aprendizado (1-based) e se já passou do prazo. */
export function learningProgress(settings, now = new Date()) {
    if (!settings.learningStartedAt)
        return { started: false, day: 0, days: settings.learningDays, finished: false };
    const elapsedDays = Math.floor((now.getTime() - Date.parse(settings.learningStartedAt)) / 86_400_000);
    const day = Math.min(settings.learningDays, Math.max(1, elapsedDays + 1));
    return { started: true, day, days: settings.learningDays, finished: elapsedDays + 1 > settings.learningDays };
}
/** Segredos que o cofre aceita. Lista fechada: nome fora dela é recusado. */
export const AI_SECRET_NAMES = ["OPENROUTER_API_KEY", "TYPESAFE_API_KEY"];
/** Mostra só o fim da chave (nunca o valor inteiro, nem para o dono). */
export function maskSecret(value) {
    if (!value)
        return null;
    return value.length <= 8 ? "••••" : `${value.slice(0, 3)}…${value.slice(-4)}`;
}
